import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "compare-libraries-container" }
const _hoisted_2 = {
  key: 0,
  class: "has-text-centered py-20"
}
const _hoisted_3 = {
  key: 1,
  class: "has-text-centered py-20"
}
const _hoisted_4 = { class: "title is-5 mb-10" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["href"]
const _hoisted_7 = ["href"]
const _hoisted_8 = {
  key: 3,
  class: "ml-5"
}
const _hoisted_9 = ["href"]

import { concat } from "lodash-es";
import { ref, computed, onMounted } from "vue";

interface User {
  id: number;
  username: string;
  slug: string;
}

interface Props {
  user1: User;
  user2: User;
  user1LibraryUrl: string;
  user2LibraryUrl: string;
}

interface GamePurchase {
  id: number;
  user_id: number;
  game: {
    id: number;
    name: string;
  };
  rating: number | null;
}

interface GameRow {
  game: {
    id: number;
    name: string;
  };
  userOneRating: number | null | undefined;
  userTwoRating: number | null | undefined;
}

interface GroupedGameRow {
  label: string;
  children: GameRow[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'compare-libraries',
  props: {
    user1: {},
    user2: {},
    user1LibraryUrl: {},
    user2LibraryUrl: {}
  },
  setup(__props: any) {

const props = __props;

// Reactive data
const user1Library = ref<GamePurchase[] | null>(null);
const user2Library = ref<GamePurchase[] | null>(null);
const groupedRows = ref<GroupedGameRow[]>([]);
const loadCheck = ref(false);
const isLoading = ref(true);
const sortColumn = ref<string>("userOneRating");
const sortDirection = ref<"asc" | "desc">("desc");

// Column definitions
const columns = computed(() => [
  {
    id: "game.name",
    label: "Game"
  },
  {
    id: "userOneRating",
    label: props.user1.username
  },
  {
    id: "userTwoRating",
    label: props.user2.username
  }
]);

// Dark mode detection
const isDarkMode = computed(() => {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
});

// Methods
function loadLibraries() {
  fetch(props.user1LibraryUrl, {
    headers: {
      "Content-Type": "application/json"
    }
  })
    .then((response) => {
      return response.json().then((json) => {
        if (response.ok) {
          return Promise.resolve(json);
        }
        return Promise.reject(json);
      });
    })
    .then((purchasedGames) => {
      user1Library.value = purchasedGames;
      loadRows();
    });

  fetch(props.user2LibraryUrl, {
    headers: {
      "Content-Type": "application/json"
    }
  })
    .then((response) => {
      return response.json().then((json) => {
        if (response.ok) {
          return Promise.resolve(json);
        }
        return Promise.reject(json);
      });
    })
    .then((purchasedGames) => {
      user2Library.value = purchasedGames;
      loadRows();
    });
}

function loadRows() {
  // Protects against the function being run when the first game library loads.
  if (loadCheck.value === false) {
    loadCheck.value = true;
    return;
  } else {
    isLoading.value = false;
  }
  groupedRows.value = getGamePurchases();
}

function gameUrl(gameId: number): string {
  return `${window.location.origin}/games/${gameId}`;
}

function userUrl(userId: number): string {
  return `${window.location.origin}/users/${userId}`;
}

function getGamePurchases(): GroupedGameRow[] {
  // Return early if both are null.
  if (user1Library.value === null && user2Library.value === null) {
    return [];
  }

  const libraries = [user1Library.value, user2Library.value].filter(Boolean) as GamePurchase[][];
  let metaLibrary = concat(...libraries);
  let betterMetaLibrary: GameRow[] = [];

  metaLibrary.forEach((gamePurchase) => {
    let isFirstUser = gamePurchase.user_id === props.user1.id;

    // If the game isn't represented in the metaLibrary yet, append it to the existing array.
    if (!betterMetaLibrary.some((gp) => gp.game.id === gamePurchase.game.id)) {
      let gameRow: GameRow = {
        game: gamePurchase.game,
        userOneRating: undefined,
        userTwoRating: undefined
      };
      if (isFirstUser) {
        gameRow.userOneRating = gamePurchase.rating;
      } else {
        gameRow.userTwoRating = gamePurchase.rating;
      }
      betterMetaLibrary.push(gameRow);
    } else {
      // If the game is already represented in the meta library, we need to modify it rather
      // than push a new entry.
      let index = betterMetaLibrary.findIndex((gp) => gp.game.id === gamePurchase.game.id);
      if (index !== -1 && betterMetaLibrary[index]) {
        if (isFirstUser) {
          betterMetaLibrary[index].userOneRating = gamePurchase.rating;
        } else {
          betterMetaLibrary[index].userTwoRating = gamePurchase.rating;
        }
      }
    }
  });

  return groupGameRows(betterMetaLibrary);
}

/**
 * Group the table into three groups:
 *   - Games owned by both users
 *   - Games only owned by user1
 *   - Games only owned by user2
 */
function groupGameRows(metaLibrary: GameRow[]): GroupedGameRow[] {
  const sharedRows: GameRow[] = [];
  const user1Rows: GameRow[] = [];
  const user2Rows: GameRow[] = [];

  for (const gameRow of metaLibrary) {
    const ownedByUser1 = gameRow.userOneRating !== undefined;
    const ownedByUser2 = gameRow.userTwoRating !== undefined;

    if (ownedByUser1 && ownedByUser2) {
      sharedRows.push(gameRow);
    } else if (ownedByUser1) {
      user1Rows.push(gameRow);
    } else if (ownedByUser2) {
      user2Rows.push(gameRow);
    }
  }

  return [
    {
      label: "Shared",
      children: sharedRows
    },
    {
      label: `Unique to ${props.user1.username}`,
      children: user1Rows
    },
    {
      label: `Unique to ${props.user2.username}`,
      children: user2Rows
    }
  ];
}

function toggleSort(columnId: string) {
  if (sortColumn.value === columnId) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortColumn.value = columnId;
    sortDirection.value = "desc";
  }
}

function sortRows(rows: GameRow[]): GameRow[] {
  return [...rows].sort((a, b) => {
    let aVal: string | number | null | undefined;
    let bVal: string | number | null | undefined;

    if (sortColumn.value === "game.name") {
      aVal = a.game.name.toLowerCase();
      bVal = b.game.name.toLowerCase();
    } else if (sortColumn.value === "userOneRating") {
      aVal = a.userOneRating;
      bVal = b.userOneRating;
    } else if (sortColumn.value === "userTwoRating") {
      aVal = a.userTwoRating;
      bVal = b.userTwoRating;
    }

    // Handle null/undefined values - always put them at the end
    if (aVal === null || aVal === undefined) {
      if (bVal === null || bVal === undefined) return 0;
      return 1;
    }
    if (bVal === null || bVal === undefined) {
      return -1;
    }

    if (aVal < bVal) return sortDirection.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection.value === "asc" ? 1 : -1;
    return 0;
  });
}

// Lifecycle
onMounted(() => {
  loadLibraries();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
          _createElementVNode("span", null, "Loading...", -1)
        ]))]))
      : (groupedRows.value.length === 0 || groupedRows.value.every((g) => g.children.length === 0))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("span", { class: "has-text-grey" }, "No games to compare.", -1)
          ]))]))
        : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(groupedRows.value, (group) => {
            return (_openBlock(), _createElementBlock("div", {
              key: group.label,
              class: "mb-20"
            }, [
              _createElementVNode("h3", _hoisted_4, _toDisplayString(group.label) + " (" + _toDisplayString(group.children.length) + ")", 1),
              (group.children.length > 0)
                ? (_openBlock(), _createElementBlock("table", {
                    key: 0,
                    class: _normalizeClass(["table is-fullwidth is-striped", { 'is-dark': isDarkMode.value }])
                  }, [
                    _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column) => {
                          return (_openBlock(), _createElementBlock("th", {
                            key: column.id,
                            class: "cursor-pointer",
                            onClick: ($event: any) => (toggleSort(column.id))
                          }, [
                            (column.id === 'userOneRating')
                              ? (_openBlock(), _createElementBlock("a", {
                                  key: 0,
                                  href: userUrl(__props.user1.id)
                                }, _toDisplayString(column.label), 9, _hoisted_6))
                              : (column.id === 'userTwoRating')
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 1,
                                    href: userUrl(__props.user2.id)
                                  }, _toDisplayString(column.label), 9, _hoisted_7))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    _createTextVNode(_toDisplayString(column.label), 1)
                                  ], 64)),
                            (sortColumn.value === column.id)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(sortDirection.value === "asc" ? "▲" : "▼"), 1))
                              : _createCommentVNode("", true)
                          ], 8, _hoisted_5))
                        }), 128))
                      ])
                    ]),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortRows(group.children), (row) => {
                        return (_openBlock(), _createElementBlock("tr", {
                          key: row.game.id
                        }, [
                          _createElementVNode("td", null, [
                            _createElementVNode("a", {
                              href: gameUrl(row.game.id)
                            }, _toDisplayString(row.game.name), 9, _hoisted_9)
                          ]),
                          _createElementVNode("td", null, _toDisplayString(row.userOneRating ?? ""), 1),
                          _createElementVNode("td", null, _toDisplayString(row.userTwoRating ?? ""), 1)
                        ]))
                      }), 128))
                    ])
                  ], 2))
                : _createCommentVNode("", true)
            ]))
          }), 128))
  ]))
}
}

})